// src/hooks/useProgress.js
import { useState, useEffect, useCallback } from 'react';
import { TOTAL_LEVELS } from '../utils/levelConfig';

const STORAGE_KEY = 'prismpals_progress_v1';

const DEFAULT_PROGRESS = {
  unlockedLevel: 1, // highest level the player may currently play
  stars: {},        // { [levelNumber]: 1|2|3 }
  coins: 0,
};

function loadProgress() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_PROGRESS };
    const parsed = JSON.parse(raw);
    return {
      unlockedLevel: parsed.unlockedLevel || 1,
      stars: parsed.stars || {},
      coins: typeof parsed.coins === 'number' ? parsed.coins : 0,
    };
  } catch (_) {
    return { ...DEFAULT_PROGRESS };
  }
}

function saveProgress(progress) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
  } catch (_) {}
}

/**
 * Central progress hook. Use anywhere progress needs to be read or written —
 * levels.jsx (cards + lock state), Nav (coin count), level pages (on win),
 * victory.jsx (display), characters.jsx (unlocks), etc.
 */
export function useProgress() {
  const [progress, setProgress] = useState(loadProgress);

  useEffect(() => {
    saveProgress(progress);
  }, [progress]);

  const isUnlocked = useCallback(
    (levelNum) => levelNum <= progress.unlockedLevel,
    [progress.unlockedLevel]
  );

  const getStars = useCallback(
    (levelNum) => progress.stars[levelNum] || 0,
    [progress.stars]
  );

  const isCompleted = useCallback(
    (levelNum) => Boolean(progress.stars[levelNum]),
    [progress.stars]
  );

  const totalStars = Object.values(progress.stars).reduce((a, b) => a + b, 0);
  const completedCount = Object.keys(progress.stars).length;

  /** Call when a level is won. Unlocks the next level, keeps best star score, adds coins. */
  const completeLevel = useCallback((levelNum, starsEarned, coinsEarned) => {
    setProgress(prev => {
      const prevStars = prev.stars[levelNum] || 0;
      const nextUnlock = Math.min(
        Math.max(prev.unlockedLevel, levelNum + 1),
        TOTAL_LEVELS
      );
      return {
        ...prev,
        unlockedLevel: nextUnlock,
        stars: { ...prev.stars, [levelNum]: Math.max(prevStars, starsEarned) },
        coins: prev.coins + coinsEarned,
      };
    });
  }, []);

  const resetProgress = useCallback(() => {
    setProgress({ ...DEFAULT_PROGRESS });
  }, []);

  return {
    progress,
    isUnlocked,
    getStars,
    isCompleted,
    totalStars,
    completedCount,
    coins: progress.coins,
    completeLevel,
    resetProgress,
  };
}